import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  SiteHeader, SiteFooter, CourtArt, Check, BIDCOURT_URL,
  STAFF_PHONE, ADMIN_PHONE, INSTAGRAM, VENUE,
} from "../components/Layout";
import { fetchBookedSlots } from "../lib/api";
import { ALL_SLOTS, isPastSlot, localISO, prettyDate, rateFor, slotStart } from "../lib/slots";

const FACTS = [
  { big: "7 AM – 12 AM", small: "Open every day" },
  { big: "From ₹300 / hr", small: "Off-peak, 9 AM to 5 PM" },
  { big: "Gear included", small: "Paddles and balls at the court" },
  { big: "30-minute slots", small: "Book online, pay at the court" },
];

const COURT = [
  "8-layer cushioned acrylic surface",
  "Floodlit for night games until midnight",
  "Paddles and balls included with every booking",
  "Open to the public every day",
];

const STEPS = [
  { n: "01", t: "Pick your slots", b: "Choose a date and one or more 30-minute slots. Free slots update live." },
  { n: "02", t: "Add your details", b: "Your name and WhatsApp number. Have a promo code? Add it here." },
  { n: "03", t: "Play and pay", b: "Your confirmation arrives on WhatsApp. Turn up, play, and pay at the court by UPI or cash." },
];

/**
 * The next six free slots: what is left today, topped up from tomorrow once
 * the evening runs out.
 */
function useNextFreeSlots(count = 6) {
  const [state, setState] = useState({ loading: true, items: [], label: "" });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const now = new Date();
      const today = localISO(now);
      const tmr = new Date(now);
      tmr.setDate(now.getDate() + 1);
      const tomorrow = localISO(tmr);

      const pick = (date, booked) =>
        ALL_SLOTS.filter((s) => !booked.includes(s) && !isPastSlot(date, s, now)).map((s) => ({ date, slot: s }));

      const bookedToday = await fetchBookedSlots(today);
      let items = pick(today, bookedToday);
      let label = `Today, ${prettyDate(today)}`;
      if (items.length < count) {
        const bookedTmr = await fetchBookedSlots(tomorrow);
        const more = pick(tomorrow, bookedTmr);
        if (!items.length) label = `Tomorrow, ${prettyDate(tomorrow)}`;
        else label = "Today and tomorrow";
        items = [...items, ...more];
      }
      if (!cancelled) setState({ loading: false, items: items.slice(0, count), label });
    })();
    return () => { cancelled = true; };
  }, [count]);
  return state;
}

function NextSlots() {
  const { loading, items, label } = useNextFreeSlots(6);
  return (
    <div className="vv-slotcard">
      <div className="vv-slotcard__head">
        <h2 className="vv-display vv-slotcard__title">Next free slots</h2>
        <span className="vv-slotcard__when">{loading ? "Checking…" : label}</span>
      </div>
      <div className="vv-slotcard__grid">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <span key={i} className="vv-slotcard__slot is-loading" />)
          : items.map(({ date, slot }) => {
              const rate = rateFor(slot);
              return (
                <Link
                  key={date + slot}
                  to={`/book?date=${date}&slot=${encodeURIComponent(slot)}`}
                  className="vv-slotcard__slot"
                >
                  <span className="vv-slotcard__time">{slotStart(slot)}</span>
                  <span className={`vv-slotcard__price ${rate === 250 ? "is-peak" : ""}`}>₹{rate}</span>
                </Link>
              );
            })}
      </div>
      <Link to="/book" className="vv-btn vv-btn--strong vv-btn--block" data-vv-surface="dark">See all times</Link>
    </div>
  );
}

export default function Home() {
  return (
    <div className="vv-page">
      <SiteHeader />

      <main>
        <section aria-labelledby="hero-h" className="vv-hero" data-vv-surface="hero">
          <div className="vv-wrap vv-hero__row">
            <div className="vv-hero__copy">
              <p className="vv-eyebrow vv-eyebrow--on-dark">Chh. Sambhajinagar's pickleball district</p>
              <h1 id="hero-h" className="vv-display vv-display--italic vv-hero__title">Play hard.<br />Stay social.</h1>
              <p className="vv-hero__lead">
                A cushioned 8-layer acrylic court, floodlit and open every day from 7 AM to midnight.
                Paddles and balls included. Book in 30-minute slots and pay at the court.
              </p>
              <div className="vv-btnrow">
                <Link to="/book" className="vv-btn vv-btn--accent vv-btn--lg">Book a court</Link>
                <Link to="/#rates" className="vv-btn vv-btn--ghost vv-btn--lg">See rates</Link>
              </div>
            </div>
            <div className="vv-hero__art">
              <CourtArt />
              <NextSlots />
            </div>
          </div>
        </section>

        <section aria-label="At a glance" className="vv-facts">
          <div className="vv-wrap vv-facts__grid">
            {FACTS.map((f) => (
              <div key={f.big}>
                <div className="vv-display vv-facts__big">{f.big}</div>
                <div className="vv-facts__small">{f.small}</div>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="court-h" className="vv-section">
          <div className="vv-wrap vv-split">
            <div className="vv-courtpanel" data-vv-surface="dark" aria-hidden="true">
              <div className="vv-courtpanel__glow" />
              <CourtArt />
              <div className="vv-courtpanel__caption">
                <span className="vv-display vv-courtpanel__big">8-layer cushioned acrylic</span>
                <span>Floodlit every night until midnight</span>
              </div>
            </div>
            <div className="vv-stack">
              <p className="vv-eyebrow">The court</p>
              <h2 id="court-h" className="vv-display vv-h2">Built for long rallies</h2>
              <p className="vv-lead">
                One court with an 8-layer cushioned acrylic surface, floodlights for late games,
                and gear waiting for you when you arrive.
              </p>
              <ul className="vv-checklist">
                {COURT.map((c) => <li key={c}><Check /><span>{c}</span></li>)}
              </ul>
            </div>
          </div>
        </section>

        <section id="rates" aria-labelledby="rates-h" className="vv-section vv-section--paper">
          <div className="vv-wrap vv-stack vv-stack--lg">
            <div className="vv-sectionhead">
              <div>
                <p className="vv-eyebrow">Rates</p>
                <h2 id="rates-h" className="vv-display vv-h2">Simple, per hour</h2>
              </div>
              <p className="vv-lead vv-sectionhead__aside">Book in 30-minute slots. Equipment included. Pay at the court by UPI or cash.</p>
            </div>
            <div className="vv-rates">
              <div className="vv-rate">
                <h3 className="vv-rate__name">Off-peak</h3>
                <p className="vv-rate__when">Every day, 9 AM to 5 PM</p>
                <p className="vv-rate__price"><span className="vv-display">₹300</span><span> / hour</span></p>
                <p className="vv-rate__slot">₹150 per 30-minute slot</p>
              </div>
              <div className="vv-rate vv-rate--strong" data-vv-surface="dark">
                <h3 className="vv-rate__name">Peak</h3>
                <p className="vv-rate__when">Mornings before 9 AM and evenings from 5 PM</p>
                <p className="vv-rate__price"><span className="vv-display">₹500</span><span> / hour</span></p>
                <p className="vv-rate__slot">₹250 per 30-minute slot</p>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="league-h" className="vv-section vv-section--strong" data-vv-surface="dark">
          <div className="vv-wrap vv-split">
            <div className="vv-stack">
              <p className="vv-eyebrow vv-eyebrow--on-dark">New · Leagues &amp; auctions</p>
              <h2 id="league-h" className="vv-display vv-display--italic vv-h2 vv-h2--xl">Run your league's player auction</h2>
              <p className="vv-lead vv-lead--on-dark">
                Players register with their DUPR rating or skill level, team owners bid live from their phones,
                and the big screen shows every sale. Squad rules are enforced for you.
              </p>
              <div className="vv-btnrow">
                <Link to="/leagues" className="vv-btn vv-btn--accent">Explore leagues &amp; auctions</Link>
                <a href={BIDCOURT_URL} className="vv-btn vv-btn--ghost">Try a demo</a>
              </div>
            </div>
            <div className="vv-lotcard" aria-hidden="true">
              <div className="vv-lotcard__top">
                <div className="vv-lotcard__avatar vv-display">KS</div>
                <div>
                  <div className="vv-lotcard__label vv-lotcard__label--link">Now bidding · Tier B</div>
                  <div className="vv-display vv-lotcard__name">Kabir Sethi</div>
                  <div className="vv-lotcard__meta">DUPR 4.12 · Right side</div>
                </div>
              </div>
              <div className="vv-lotcard__bottom">
                <div>
                  <div className="vv-lotcard__label">Current bid</div>
                  <div className="vv-display vv-lotcard__bid">11,000</div>
                </div>
                <span className="vv-teamchip"><span className="vv-teamchip__dot" style={{ background: "#7CC4FF" }} />Kitchen Kings</span>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="how-h" className="vv-section">
          <div className="vv-wrap vv-stack vv-stack--lg">
            <div>
              <p className="vv-eyebrow">How booking works</p>
              <h2 id="how-h" className="vv-display vv-h2">On court in three steps</h2>
            </div>
            <ol className="vv-steps">
              {STEPS.map((s) => (
                <li key={s.n}>
                  <span className="vv-display vv-steps__n">{s.n}</span>
                  <h3>{s.t}</h3>
                  <p>{s.b}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="visit" aria-labelledby="visit-h" className="vv-section vv-section--paper">
          <div className="vv-wrap vv-split vv-split--top">
            <div className="vv-stack">
              <p className="vv-eyebrow">Visit us</p>
              <h2 id="visit-h" className="vv-display vv-h2">Find the court</h2>
              <div className="vv-facts-list">
                <div className="vv-facts-list__wide"><h3>Address</h3><p>{VENUE.lines[0]}<br />{VENUE.lines[1]}</p></div>
                <div><h3>Open</h3><p>Every day<br />7:00 AM to 12:00 AM</p></div>
                <div><h3>On-site staff</h3><p><a href={STAFF_PHONE.href}>{STAFF_PHONE.label}</a></p></div>
                <div><h3>Admin</h3><p><a href={ADMIN_PHONE.href}>{ADMIN_PHONE.label}</a></p></div>
              </div>
              <div className="vv-btnrow">
                <a href={VENUE.directions} target="_blank" rel="noopener noreferrer" className="vv-btn vv-btn--strong" data-vv-surface="dark">
                  Get directions
                </a>
                <Link to="/book" className="vv-btn vv-btn--outline">Book a court</Link>
              </div>
              <a href={INSTAGRAM.href} target="_blank" rel="noopener noreferrer" className="vv-visit__ig">
                Follow {INSTAGRAM.label} on Instagram
              </a>
            </div>
            <div className="vv-map">
              <iframe
                title="Map: Vibe & Volley, near ISKCON Temple, Chintamani Colony, Chhatrapati Sambhajinagar"
                src={VENUE.embed}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
